import { Embeddable, GenericEmbedded, isEmbedded } from './embedded.js';
import type { Value } from './values.js';

import { Annotated } from './annotated.js';
import { Bytes } from './bytes.js';
import { KeyedDictionary, KeyedSet } from './dictionary.js';
import { Writer, WriterOptions, WriterState } from './writer.js';
import { fromJS } from './fromjs.js';
import { Reader, ReaderOptions } from './reader.js';

export function parse<T extends Embeddable = GenericEmbedded>(
    buffer: string,
    options?: ReaderOptions<T>,
): Value<T> {
    return new Reader<T>(buffer, options).next();
}

export function parseAll<T extends Embeddable = GenericEmbedded>(
    buffer: string,
    options?: ReaderOptions<T>,
): Value<T>[] {
    return new Reader<T>(buffer, options).readToEnd();
}

export const stringifyEmbeddedWrite = {
    write(s: WriterState, v: any): void {
        if (v instanceof GenericEmbedded) {
            new Writer(s, this).push(v.generic);
        } else {
            try {
                const j = fromJS(v);
                if (!isEmbedded(j)) {
                    new Writer(s, this).push(j);
                    return;
                }
            } catch {}
            try {
                s.pieces.push(JSON.stringify(v));
                return;
            } catch {}
            s.pieces.push('⌜' + v + '⌝');
        }
    }
};

export function stringify<T extends Embeddable = GenericEmbedded>(x: any, options?: WriterOptions<T>): string {
    options = { ... (options ?? {}) };
    options.embeddedWrite = options.embeddedWrite ?? stringifyEmbeddedWrite;
    return Writer.stringify(fromJS<T>(x), options);
}

export function preserves(pieces: TemplateStringsArray, ...values: any[]): string {
    const result = [pieces[0]];
    values.forEach((v, i) => {
        result.push(stringify(v));
        result.push(pieces[i + 1]);
    });
    return result.join('');
}
